<template>
  <div class="topic-page">
    <section class="topic-toolbar">
      <div class="topic-toolbar__copy">
        <div class="topic-toolbar__title">广场话题</div>
        <div class="topic-toolbar__desc">
          这里维护的话题会同步到小程序广场的筛选栏、发帖页的话题选择、帖子详情和我的帖子。停用的话题不再出现在发帖与筛选中，历史帖子仍会显示原话题名。
        </div>
      </div>
      <div class="topic-toolbar__actions">
        <span class="topic-count">{{ topics.length }} 个话题</span>
        <button class="ghost-button" type="button" :disabled="loading" @click="reload">
          {{ loading ? "刷新中" : "刷新" }}
        </button>
        <button class="primary-button" type="button" @click="startCreate">新增话题</button>
      </div>
    </section>

    <section class="topic-workbench">
      <aside class="topic-list" aria-label="话题列表">
        <div v-if="loading" class="list-state">正在加载话题…</div>
        <div v-else-if="!topics.length" class="list-state">还没有话题，点右上角新增。</div>
        <div v-else class="list-body">
          <div
            v-for="(item, index) in topics"
            :key="String(item.id)"
            class="topic-item"
            :class="{ 'topic-item--active': form.id === item.id }"
            @click="selectTopic(item)"
          >
            <div class="topic-item__body">
              <div class="topic-item__name">
                <span class="topic-hash">#</span>{{ item.topicName }}
              </div>
              <div class="topic-item__meta">
                {{ item.topicCode }} · {{ item.postCount || 0 }} 条帖子
              </div>
            </div>
            <div class="topic-item__side">
              <span class="status-dot" :class="item.status === 1 ? 'status-dot--on' : 'status-dot--off'"></span>
              <div class="order-buttons">
                <button
                  type="button"
                  class="order-button"
                  :disabled="index === 0 || moving"
                  title="上移"
                  @click.stop="move(item, 'UP')"
                >↑</button>
                <button
                  type="button"
                  class="order-button"
                  :disabled="index === topics.length - 1 || moving"
                  title="下移"
                  @click.stop="move(item, 'DOWN')"
                >↓</button>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <article class="topic-editor">
        <header class="editor-header">
          <div>
            <div class="editor-header__eyebrow">
              {{ form.id ? "正在编辑 #" + form.id : "新增" }}
            </div>
            <h2>{{ form.id ? ("#" + (form.topicName || "未命名")) : "新增话题" }}</h2>
          </div>
          <span v-if="form.id" class="editor-header__hint">
            更新于 {{ formatDateTime(activeTopic && activeTopic.updatedAt) }}
          </span>
        </header>

        <div class="editor-body">
          <section class="editor-section">
            <div class="section-heading">
              <div>
                <h3>话题信息</h3>
                <p>名称会显示成「#名称」，出现在广场筛选、发帖页和帖子详情。</p>
              </div>
            </div>
            <label class="field">
              <span class="field__label">话题名称</span>
              <input v-model.trim="form.topicName" type="text" maxlength="32" placeholder="例如：喂养经验" />
            </label>
            <label class="field">
              <span class="field__label">话题编码</span>
              <input
                v-model.trim="form.topicCode"
                type="text"
                maxlength="32"
                :disabled="Boolean(form.id)"
                placeholder="例如：feeding"
              />
              <span class="field__hint" v-if="form.id">
                编码已锁定：历史帖子按编码归类，改动会导致老帖子对不上话题。
              </span>
              <span class="field__hint" v-else>
                小写字母、数字和连字符，以字母开头。保存后不可修改。
              </span>
            </label>
            <label class="field">
              <span class="field__label">说明文案 <small>选填</small></span>
              <input v-model.trim="form.guidance" type="text" maxlength="128" placeholder="例如：食物、补水与日常照护" />
              <span class="field__hint">发帖页选择话题时会显示这句说明。</span>
            </label>
          </section>

          <section class="editor-section">
            <div class="section-heading">
              <div>
                <h3>展示状态</h3>
                <p>停用后不再出现在发帖和筛选中，历史帖子不受影响。</p>
              </div>
            </div>
            <label class="switch-row">
              <input v-model="statusEnabled" type="checkbox" />
              <span>{{ statusEnabled ? "已启用" : "已停用" }}</span>
            </label>
          </section>
        </div>

        <footer class="editor-footer">
          <div class="editor-footer__state">
            <span v-if="message" :class="{ 'text-error': messageError }">{{ message }}</span>
            <span v-else>保存后小程序下次进入相关页面即可看到。</span>
          </div>
          <div class="editor-footer__actions">
            <button v-if="form.id" class="danger-button" type="button" :disabled="saving" @click="removeCurrent">
              删除
            </button>
            <button v-if="form.id" class="ghost-button" type="button" :disabled="saving" @click="startCreate">
              改为新增
            </button>
            <button class="primary-button" type="button" :disabled="saving" @click="submit">
              {{ saving ? "保存中…" : "保存" }}
            </button>
          </div>
        </footer>
      </article>
    </section>
  </div>
</template>

<script>
import {
  deleteSquareTopic,
  fetchSquareTopics,
  moveSquareTopic,
  saveSquareTopic
} from "../api/squareTopic"

function emptyForm() {
  return {
    id: null,
    topicCode: "",
    topicName: "",
    guidance: "",
    status: 1
  }
}

export default {
  name: "SquareTopicView",
  data() {
    return {
      loading: false,
      saving: false,
      moving: false,
      message: "",
      messageError: false,
      topics: [],
      form: emptyForm()
    }
  },
  computed: {
    activeTopic() {
      if (!this.form.id) return null
      return this.topics.find(item => item.id === this.form.id) || null
    },
    statusEnabled: {
      get() {
        return this.form.status === 1
      },
      set(value) {
        this.form.status = value ? 1 : 0
      }
    }
  },
  created() {
    this.reload()
  },
  methods: {
    async reload() {
      this.loading = true
      try {
        const res = await fetchSquareTopics()
        this.topics = Array.isArray(res.data) ? res.data : []
        if (this.form.id) {
          const current = this.topics.find(item => item.id === this.form.id)
          if (current) this.selectTopic(current)
          else this.form = emptyForm()
        }
      } catch (error) {
        this.topics = []
        this.flash((error && error.message) || "话题加载失败", true)
      } finally {
        this.loading = false
      }
    },
    startCreate() {
      this.form = emptyForm()
      this.message = ""
      this.messageError = false
    },
    selectTopic(item) {
      this.form = {
        id: item.id,
        topicCode: item.topicCode || "",
        topicName: item.topicName || "",
        guidance: item.guidance || "",
        status: item.status === 0 ? 0 : 1
      }
      this.message = ""
      this.messageError = false
    },
    async submit() {
      if (!this.form.topicName) {
        this.flash("请填写话题名称", true)
        return
      }
      if (!this.form.id && !this.form.topicCode) {
        this.flash("请填写话题编码", true)
        return
      }
      this.saving = true
      try {
        const res = await saveSquareTopic({
          id: this.form.id,
          topicCode: this.form.id ? undefined : this.form.topicCode,
          topicName: this.form.topicName,
          guidance: this.form.guidance || null,
          status: this.form.status
        })
        this.flash(res.message || "已保存")
        const savedId = res.data && res.data.id
        await this.reload()
        if (savedId) {
          const saved = this.topics.find(item => item.id === savedId)
          if (saved) this.selectTopic(saved)
        }
      } catch (error) {
        this.flash((error && error.message) || "保存失败", true)
      } finally {
        this.saving = false
      }
    },
    async removeCurrent() {
      if (!this.form.id) return
      const name = this.form.topicName || "这个话题"
      if (!window.confirm(`确认删除「#${name}」？删除后发帖页不再提供该话题。`)) return
      this.saving = true
      try {
        await deleteSquareTopic(this.form.id)
        this.flash("已删除")
        this.form = emptyForm()
        await this.reload()
      } catch (error) {
        this.flash((error && error.message) || "删除失败", true)
      } finally {
        this.saving = false
      }
    },
    async move(item, direction) {
      if (this.moving) return
      this.moving = true
      try {
        const res = await moveSquareTopic(item.id, direction)
        if (Array.isArray(res.data)) this.topics = res.data
        else await this.reload()
      } catch (error) {
        this.flash((error && error.message) || "调整顺序失败", true)
      } finally {
        this.moving = false
      }
    },
    flash(text, isError = false) {
      this.message = text
      this.messageError = isError
    },
    formatDateTime(value) {
      if (!value) return "-"
      return String(value).replace("T", " ").slice(0, 16)
    }
  }
}
</script>

<style scoped>
.topic-page {
  display: flex;
  flex-direction: column;
  gap: 18px;
  height: 100%;
  color: #17221d;
}

.topic-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 20px 22px;
  background: #fff;
  border: 1px solid #e5ebe7;
  border-radius: 16px;
  box-shadow: 0 10px 26px rgba(23, 34, 29, 0.05);
  flex-wrap: wrap;
}

.topic-toolbar__title {
  font-size: 20px;
  font-weight: 700;
}

.topic-toolbar__desc {
  margin-top: 6px;
  max-width: 660px;
  color: #6c7a73;
  font-size: 13px;
  line-height: 1.7;
}

.topic-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.topic-count {
  padding: 5px 12px;
  border-radius: 999px;
  background: #f2f5f3;
  color: #4d5c54;
  font-size: 12px;
}

.ghost-button,
.primary-button,
.danger-button {
  height: 36px;
  padding: 0 16px;
  border-radius: 10px;
  font-size: 13px;
  cursor: pointer;
}

.ghost-button {
  border: 1px solid #dfe6e1;
  background: #fff;
  color: #33463b;
}

.primary-button {
  border: 0;
  background: #0fbd68;
  color: #fff;
}

.danger-button {
  border: 1px solid #f0cdc5;
  background: #fff;
  color: #b23f2c;
}

.ghost-button:disabled,
.primary-button:disabled,
.danger-button:disabled,
.order-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.topic-workbench {
  display: grid;
  grid-template-columns: 340px minmax(0, 1fr);
  gap: 18px;
  flex: 1;
  min-height: 0;
}

.topic-list,
.topic-editor {
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #fff;
  border: 1px solid #e5ebe7;
  border-radius: 16px;
  box-shadow: 0 10px 26px rgba(23, 34, 29, 0.05);
}

.list-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.list-state {
  padding: 44px 20px;
  color: #86928c;
  font-size: 13px;
  text-align: center;
}

.topic-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
}

.topic-item:hover {
  background: #f7faf8;
}

.topic-item--active {
  background: #eefaf2;
}

.topic-item__body {
  flex: 1;
  min-width: 0;
}

.topic-item__name {
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.topic-hash {
  color: #0fbd68;
  margin-right: 2px;
}

.topic-item__meta {
  margin-top: 4px;
  color: #86928c;
  font-size: 12px;
}

.topic-item__side {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-dot--on {
  background: #0fbd68;
}

.status-dot--off {
  background: #cdd8d2;
}

.order-buttons {
  display: flex;
  gap: 4px;
}

.order-button {
  width: 24px;
  height: 24px;
  border: 1px solid #dfe6e1;
  border-radius: 6px;
  background: #fff;
  color: #4d5c54;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
}

.editor-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
  border-bottom: 1px solid #eef2ef;
  flex-wrap: wrap;
}

.editor-header__eyebrow {
  color: #86928c;
  font-size: 12px;
}

.editor-header h2 {
  margin: 6px 0 0;
  font-size: 19px;
}

.editor-header__hint {
  color: #86928c;
  font-size: 12px;
}

.editor-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.editor-section {
  padding: 16px 18px;
  border: 1px solid #eef2ef;
  border-radius: 14px;
}

.section-heading {
  margin-bottom: 14px;
}

.section-heading h3 {
  margin: 0;
  font-size: 15px;
}

.section-heading p {
  margin: 4px 0 0;
  color: #86928c;
  font-size: 12px;
  line-height: 1.7;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
}

.field__label {
  font-size: 13px;
  color: #33463b;
}

.field__label small {
  color: #97a29c;
  font-size: 12px;
}

.field input[type="text"] {
  height: 38px;
  padding: 0 12px;
  border: 1px solid #dfe6e1;
  border-radius: 10px;
  background: #fff;
  color: #1f2d26;
  font-size: 13px;
  outline: none;
}

.field input[type="text"]:disabled {
  background: #f6f8f6;
  color: #86928c;
}

.field__hint {
  color: #97a29c;
  font-size: 12px;
  line-height: 1.7;
}

.switch-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #33463b;
}

.switch-row input {
  width: 16px;
  height: 16px;
}

.editor-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 22px;
  border-top: 1px solid #eef2ef;
  flex-wrap: wrap;
}

.editor-footer__state {
  color: #6c7a73;
  font-size: 13px;
}

.text-error {
  color: #b23f2c;
}

.editor-footer__actions {
  display: flex;
  gap: 10px;
}

@media (max-width: 1080px) {
  .topic-workbench {
    grid-template-columns: 1fr;
  }
}
</style>
